import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ViewportService} from '../../../services/viewport.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {DateTime} from 'luxon';
import {ItineraryDealView} from '../../../models/DealsModels';

interface CalendarDay {
    date: Date;
    dayNumber: number;
    isCurrentMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    hasDeals: boolean;
    lowestPrice?: number;
    deals: ItineraryDealView[];
}

interface CalendarMonth {
    month: number;
    year: number;
    monthName: string;
    weeks: CalendarDay[][];
}

@Component({
    selector: 'itinerary-calendar',
    templateUrl: './itinerary-calendar.component.html',
    styleUrls: ['./itinerary-calendar.component.css']
})
export class ItineraryCalendarComponent implements OnInit, OnDestroy {
    @Input() itineraries: ItineraryDealView[] = [];
    @Output() itinerarySelected = new EventEmitter<ItineraryDealView>();
    @Output() bookingModalRequested = new EventEmitter<ItineraryDealView>();

    currentDate: Date = new Date();
    selectedDate: Date | null = null;
    selectedDeals: ItineraryDealView[] = [];
    calendarMonths: CalendarMonth[] = [];
    isPreviousDisabled: boolean = false;
    isNextDisabled: boolean = false;

    private destroy$ = new Subject<void>();

    constructor(public viewportService: ViewportService) {}

    ngOnInit() {
        // Set initial month to first deal's month
        this.setInitialMonth();
        this.generateCalendar();
        this.selectFirstDealDate();
        this.updateNavigationState();

        // Regenerate calendar when viewport changes between mobile and desktop
        this.viewportService.isMobile$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.generateCalendar();
                this.updateNavigationState();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    setInitialMonth() {
        // Find the earliest departure date from all itineraries
        let earliestDt: DateTime | null = null;

        for (const itinerary of this.itineraries) {
            const startDt = DateTime.fromISO(itinerary.startDate, { zone: 'utc' });
            if (startDt.isValid) {
                if (!earliestDt || startDt < earliestDt) {
                    earliestDt = startDt;
                }
            }
        }

        // Set currentDate to the earliest deal month, or keep current date if no deals
        if (earliestDt) {
            this.currentDate = earliestDt.startOf('month').toJSDate();
        }
    }

    generateCalendar() {
        this.calendarMonths = [];
        const monthsToShow = this.viewportService.isMobile ? 1 : 2;

        for (let i = 0; i < monthsToShow; i++) {
            const monthDt = DateTime.fromJSDate(this.currentDate).plus({ months: i }).startOf('month');
            this.calendarMonths.push(this.generateMonth(monthDt.toJSDate()));
        }
    }

    selectFirstDealDate() {
        // Flatten all days and find first with deals
        const allDays = this.calendarMonths.flatMap(month =>
            month.weeks.flatMap(week => week)
        );

        const firstDealDay = allDays.find(day => day.hasDeals && day.isCurrentMonth);

        if (firstDealDay) {
            this.selectedDate = firstDealDay.date;

            // Set selected deals to all deals for this date, sorted by price
            if (firstDealDay.deals.length > 0) {
                const sortedDeals = [...firstDealDay.deals].sort((a, b) => a.price - b.price);
                this.selectedDeals = sortedDeals;
            }

            this.generateCalendar();
        }
    }

    generateMonth(date: Date): CalendarMonth {
        const dt = DateTime.fromJSDate(date).startOf('month');
        const year = dt.year;
        const month = dt.month - 1; // JavaScript month (0-11)
        const monthName = dt.toFormat('LLLL yyyy');

        const startingDayOfWeek = dt.weekday % 7; // Luxon: 1-7 (Mon-Sun), convert to 0-6 (Sun-Sat)
        const daysInMonth = dt.daysInMonth;

        const weeks: CalendarDay[][] = [];
        let week: CalendarDay[] = [];

        // Fill in days from previous month
        const prevMonth = dt.minus({ months: 1 });
        const prevMonthDaysToShow = startingDayOfWeek;
        for (let i = prevMonthDaysToShow - 1; i >= 0; i--) {
            const dayDt = prevMonth.endOf('month').minus({ days: i });
            week.push(this.createCalendarDay(dayDt.toJSDate(), false));
        }

        // Fill in days of current month
        for (let day = 1; day <= daysInMonth; day++) {
            const dayDt = dt.set({ day });
            week.push(this.createCalendarDay(dayDt.toJSDate(), true));

            if (week.length === 7) {
                weeks.push(week);
                week = [];
            }
        }

        // Fill in days from next month
        if (week.length > 0) {
            const remainingDays = 7 - week.length;
            const nextMonth = dt.plus({ months: 1 }).startOf('month');
            for (let day = 0; day < remainingDays; day++) {
                const dayDt = nextMonth.plus({ days: day });
                week.push(this.createCalendarDay(dayDt.toJSDate(), false));
            }
            weeks.push(week);
        }

        return {
            month,
            year,
            monthName,
            weeks
        };
    }

    createCalendarDay(date: Date, isCurrentMonth: boolean): CalendarDay {
        const dt = DateTime.fromJSDate(date);
        const today = DateTime.now();
        const isToday = dt.hasSame(today, 'day');
        const isSelected = this.selectedDate ? dt.hasSame(DateTime.fromJSDate(this.selectedDate), 'day') : false;
        const deals = this.getDealsForDate(date);
        const hasDeals = deals.length > 0;
        const lowestPrice = hasDeals ? Math.min(...deals.map(d => d.price)) : undefined;

        return {
            date,
            dayNumber: dt.day,
            isCurrentMonth,
            isToday,
            isSelected,
            hasDeals,
            lowestPrice,
            deals
        };
    }

    getDealsForDate(date: Date): ItineraryDealView[] {
        const dt = DateTime.fromJSDate(date);

        return this.itineraries.filter(itinerary => {
            const startDt = DateTime.fromISO(itinerary.startDate, { zone: 'utc' });
            if (!startDt.isValid) return false;

            // Check if the date is the departure date
            return dt.hasSame(startDt, 'day');
        });
    }

    navigatePrevious() {
        const dt = DateTime.fromJSDate(this.currentDate).minus({ months: 1 }).startOf('month');
        this.currentDate = dt.toJSDate();
        this.generateCalendar();
        this.updateNavigationState();
    }

    navigateNext() {
        const dt = DateTime.fromJSDate(this.currentDate).plus({ months: 1 }).startOf('month');
        this.currentDate = dt.toJSDate();
        this.generateCalendar();
        this.updateNavigationState();
    }

    selectDay(day: CalendarDay) {
        if (!day.hasDeals || !day.isCurrentMonth) return;

        this.selectedDate = day.date;

        // Set selected deals to all deals for this date, sorted by price
        if (day.deals.length > 0) {
            const sortedDeals = [...day.deals].sort((a, b) => a.price - b.price);
            this.selectedDeals = sortedDeals;
            // Emit the first (cheapest) deal for backward compatibility
            this.itinerarySelected.emit(sortedDeals[0]);
        }

        this.generateCalendar();
    }

    openBookingModal(deal: ItineraryDealView) {
        this.bookingModalRequested.emit(deal);
    }

    getWeekdayHeaders(): string[] {
        return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    }

    updateNavigationState() {
        // Find the earliest and latest dates with deals
        let earliestDealDt: DateTime | null = null;
        let latestDealDt: DateTime | null = null;

        for (const itinerary of this.itineraries) {
            const startDt = DateTime.fromISO(itinerary.startDate, { zone: 'utc' });
            if (startDt.isValid) {
                if (!earliestDealDt || startDt < earliestDealDt) {
                    earliestDealDt = startDt;
                }
                if (!latestDealDt || startDt > latestDealDt) {
                    latestDealDt = startDt;
                }
            }
        }

        if (!earliestDealDt || !latestDealDt) {
            // No deals found, disable both buttons
            this.isPreviousDisabled = true;
            this.isNextDisabled = true;
            return;
        }

        // Get the first day of the currently displayed month(s)
        const currentMonthStart = DateTime.fromJSDate(this.currentDate).startOf('month');

        // Get the last day of the last displayed month
        const monthsToShow = this.viewportService.isMobile ? 1 : 2;
        const lastDisplayedMonthEnd = currentMonthStart.plus({ months: monthsToShow - 1 }).endOf('month');

        // Disable previous if the earliest deal is in or after the currently displayed month
        const earliestDealMonthStart = earliestDealDt.startOf('month');
        this.isPreviousDisabled = currentMonthStart <= earliestDealMonthStart;

        // Disable next if the latest deal is in or before the last displayed month
        const latestDealMonthEnd = latestDealDt.endOf('month');
        this.isNextDisabled = lastDisplayedMonthEnd >= latestDealMonthEnd;
    }
}
